import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICES, CONTACT } from "../data";
import { useQuote } from "../context/QuoteContext";

const PAGE_LINKS = [
  { label: "Wat is softwash?", to: "/softwash-methode" },
  { label: "Voor & na", to: "/#resultaten" },
  { label: "Over SoftPro", to: "/over-softpro" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { openQuote } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkCls = `text-sm font-medium transition-colors duration-300 hover:text-teal ${
    scrolled ? "text-ink/70" : "text-white/80"
  }`;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className="fixed top-0 inset-x-0 z-50 px-4 sm:px-6 lg:px-10 pt-3 sm:pt-4"
      data-testid="navbar"
    >
      <div
        className={`mx-auto max-w-7xl flex items-center justify-between rounded-full border px-4 sm:px-6 py-3 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-border shadow-[0_8px_30px_rgba(27,95,199,0.12)]"
            : "bg-navy-deep/50 backdrop-blur-md border-white/10"
        }`}
      >
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img
            src={scrolled ? "/logo.png" : "/logo-white.png"}
            alt="SoftPro logo — dak- en gevelreiniging met softwash"
            className="h-9 sm:h-10 w-auto transition-opacity duration-500"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              className={`flex items-center gap-1 ${linkCls}`}
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
              data-testid="nav-diensten"
            >
              Diensten <ChevronDown className="h-4 w-4" />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-1/2 top-full -translate-x-1/2 pt-4"
                >
                  <div className="w-64 rounded-2xl border border-border bg-white p-2 shadow-[0_20px_50px_rgba(27,95,199,0.15)]">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.id}
                        to={s.path}
                        data-testid={`nav-service-${s.id}`}
                        className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-ink/80 transition-colors hover:bg-secondary hover:text-teal"
                      >
                        <s.icon className="h-4 w-4 text-teal" />
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {PAGE_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/[/#]/g, "") || "home"}`}
              className={linkCls}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => openQuote()}
            data-testid="nav-cta-desktop"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 hover:bg-teal/90"
          >
            Gratis offerte aanvragen
          </button>
          <button
            onClick={() => setOpen(true)}
            aria-label="Menu openen"
            data-testid="menu-open"
            className={`lg:hidden grid place-items-center h-10 w-10 rounded-full ${
              scrolled ? "text-navy" : "text-white"
            }`}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[55] flex items-start justify-end bg-ink/60 backdrop-blur-sm p-3 sm:p-4 lg:hidden"
            data-testid="mobile-menu"
          >
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="flex max-h-[85vh] w-full max-w-sm flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
                <img src="/logo.png" alt="SoftPro logo" className="h-8 w-auto" />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Menu sluiten"
                  data-testid="menu-close"
                  className="grid h-10 w-10 place-items-center rounded-full bg-secondary text-ink transition-colors hover:bg-slate-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <nav
                data-lenis-prevent
                className="flex-1 overflow-y-auto overscroll-contain px-4 py-4"
              >
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-widest text-navy">
                  Diensten
                </p>
                <div className="space-y-0.5">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.id}
                      to={s.path}
                      className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-ink transition-colors hover:bg-secondary"
                    >
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-navy/10 text-navy">
                        <s.icon className="h-5 w-5" />
                      </span>
                      <span className="font-display text-base font-semibold">
                        {s.title}
                      </span>
                    </Link>
                  ))}
                </div>

                <div className="my-3 h-px bg-border" />

                <div className="space-y-0.5">
                  {PAGE_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="block rounded-2xl px-3 py-2.5 font-display text-base font-semibold text-ink transition-colors hover:bg-secondary"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </nav>

              <div className="shrink-0 border-t border-border px-4 py-4">
                <button
                  onClick={() => {
                    setOpen(false);
                    openQuote();
                  }}
                  data-testid="mobile-cta"
                  className="flex w-full items-center justify-center rounded-full bg-teal px-6 py-3.5 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Gratis offerte aanvragen
                </button>
                <a
                  href={CONTACT.phoneHref}
                  className="mt-2.5 block text-center text-sm text-muted-foreground"
                >
                  of bel {CONTACT.phone}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
